import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PageHelper } from 'Components/PageHelper';
import { Main as Container } from 'Components/Main';
import { Dialog, Select, TextField, PageHeader, Button, EditSvg } from 'Components/ui';
import { NoData } from 'Components/NoData';
import { PageLoaderList } from 'Components/PageLoader/List';
import { deletePolicy, getPoliciesPaged } from 'services/policies-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';

const STATUS_STYLE = {
  'Publicado':  { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  'Aprovado':   { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  'Em revisão': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  'Arquivado':  { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
  'Iniciado':   { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

const buildPageNumbers = (current, total) => {
  if (total <= 1) return [1];
  const pages = [];
  const start = Math.max(1, current - 2);
  const end   = Math.min(total, current + 2);
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
};

/* ── Styled Components ── */

const PageWrap = styled.div`
  padding-top: 24px;
  padding-bottom: 32px;
`;


const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid var(--color-brand);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const BannerTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

const BannerDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.55;
`;

const FilterCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
`;

// FilterActions + ClearBtn: same pattern as Users/index.jsx.
const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ClearBtn = styled.button`
  height: 40px;
  padding: 0 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-slate);
  background: white;
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: var(--vo-mist); }
`;

const FilterBtn = styled.button`
  height: 40px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:hover { background: var(--color-brand-strong); }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${p => p.$center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  background: var(--vo-mist);
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  padding: 13px 16px;
`;

const CellName = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-ink);
  font-weight: 500;
`;

const CellVersion = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  font-weight: 500;
`;

const CellDate = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

const StatusBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
`;

const ActionBtns = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;


const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid var(--vo-mist);
`;

const PaginationInfo = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

const PaginationBtns = styled.div`
  display: flex;
  gap: 4px;
`;

const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${p => p.$active ? 'none' : '1px solid var(--vo-line)'};
  background: ${p => p.$active ? 'var(--color-brand)' : 'white'};
  color: ${p => p.$active ? 'white' : 'var(--vo-ink-2)'};
  font-weight: ${p => p.$active ? '700' : '400'};
  padding: 0;
  &:disabled { opacity: .4; cursor: default; }
`;

/* ── SVG Icons ── */

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconFile = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const IconSearch = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const IconChevLeft = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconChevRight = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

/* ── Component ── */

export const Policies = () => {
  const { t } = useTranslation(['policy']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');

  const {
    isLoading,
    error,
    data: pagedPolicies,
  } = useQuery(['getPolicy', refetch, page, appliedSearch, appliedStatus], async () => {
    const result = await getPoliciesPaged(selectedTenant.slug, page, 15, appliedSearch, appliedStatus);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return { data: [], page, totalPages: 0, total: 0 };
  });

  const applyFilter = () => {
    setAppliedSearch(searchInput);
    setAppliedStatus(statusFilter);
    setPage(1);
    setRefetch(r => !r);
  };

  const handleClear = () => {
    setSearchInput(''); setStatusFilter('');
    setAppliedSearch(''); setAppliedStatus('');
    setPage(1);
  };

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deletePolicy(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!refetch);
        Dialog.alert({
          title: t('policy:page.delete.successTitle'),
          message: t('policy:page.delete.success'),
          icon: 'success',
        });
      } else {
        throw new Error(t('policy:page.delete.errorTitle'));
      }
    } catch (err) {
      Dialog.alert({
        title: t('policy:page.delete.errorTitle'),
        message: err.response?.data?.message ?? err.message,
        icon: 'error',
      });
    }
  };

  const tourSteps = [
    { target: 'body', content: t('policy:page.tour.welcome'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="add-button"]', content: t('policy:page.tour.addButton'), disableScrolling: true },
    { target: '[data-tour="filter-card"]', content: t('policy:page.tour.filterCard'), disableScrolling: true },
    { target: '[data-tour="filter-name"]', content: t('policy:page.tour.filterName'), disableScrolling: false },
    { target: '[data-tour="filter-button"]', content: t('policy:page.tour.filterButton'), disableScrolling: false },
    { target: '[data-tour="table"]', content: t('policy:page.tour.table'), disableScrolling: false },
    { target: '[data-tour="pagination"]', content: t('policy:page.tour.pagination'), disableScrolling: false },
    { target: 'body', content: t('policy:page.tour.complete'), disableScrolling: true, placement: 'center' },
  ];

  const policies = pagedPolicies?.data || [];
  const totalPages = pagedPolicies?.totalPages || 0;
  const totalItems = pagedPolicies?.total || policies.length;
  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <Container>
      <PageWrap>
        {/* Page Header */}
        <PageHeader
          title={t('policy:page.title')}
          segments={[
            { label: t('common:breadcrumbs.compliance') },
            { label: t('policy:page.title') },
          ]}
          actions={
            <span data-tour="add-button">
              <Button variant="primary" onClick={() => navigate('/policies/add')} startIcon={<IconPlus />} sx={{ height: '44px', padding: '0 20px' }}>
                {t('policy:page.addButton')}
              </Button>
            </span>
          }
        />

        {/* Info Banner */}
        <InfoBanner>
          <IconFile />
          <div>
            <BannerTitle>{t('policy:page.infoBanner.title')}</BannerTitle>
            <BannerDesc>{t('policy:page.infoBanner.description')}</BannerDesc>
          </div>
        </InfoBanner>

        {/* Filter Card */}
        {!error && (
          <FilterCard data-tour="filter-card">
            <FilterGrid>
              <FilterField data-tour="filter-name">
                <FilterLabel>{t('policy:page.filter.nameLabel')}</FilterLabel>
                <TextField
                  fullWidth
                  placeholder={t('policy:page.filter.namePlaceholder')}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyFilter()}
                  autoComplete="off"
                />
              </FilterField>
              <FilterField>
                <FilterLabel>{t('policy:page.filter.statusLabel')}</FilterLabel>
                <Select
                  name="statusFilter"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: '', label: t('policy:page.filter.selectStatus') },
                    { value: 'all', label: t('policy:page.filter.statusAll') },
                    // The 5 real stored statuses (matched server-side against lastPolicyVersionStatus)
                    { value: 'Iniciado', label: t('policy:page.filter.statusStarted') },
                    { value: 'Concluído', label: t('policy:page.filter.statusCompleted') },
                    { value: 'Revisado', label: t('policy:page.filter.statusReviewed') },
                    { value: 'Aprovado', label: t('policy:page.filter.statusApproved') },
                    { value: 'Publicado', label: t('policy:page.filter.statusPublished') },
                  ]}
                />
              </FilterField>
            </FilterGrid>
            <FilterActions>
              <ClearBtn type="button" onClick={handleClear}>
                {t('policy:page.filter.clear')}
              </ClearBtn>
              <FilterBtn data-tour="filter-button" onClick={applyFilter}>
                <IconSearch />
                {t('policy:page.search.button')}
              </FilterBtn>
            </FilterActions>
          </FilterCard>
        )}

        {/* Loading */}
        {isLoading && <PageLoaderList />}

        {/* Error */}
        {error && <div>{t('policy:page.error')} {error.message}</div>}

        {/* No Data */}
        {!isLoading && !error && policies.length === 0 && (
          <NoData description={t('policy:page.noData')} />
        )}

        {/* Table */}
        {!isLoading && !error && policies.length > 0 && (
          <TableCard data-tour="table">
            <StyledTable>
              <thead>
                <tr>
                  <Th>{t('policy:page.table.name')}</Th>
                  <Th>{t('policy:page.table.version')}</Th>
                  <Th>{t('policy:page.table.published')}</Th>
                  <Th>{t('policy:page.table.status')}</Th>
                  <Th $center>{t('policy:page.table.actions', 'Ações')}</Th>
                </tr>
              </thead>
              <tbody>
                {policies.map(item => {
                  const status = item.lastPolicyVersionStatus || '';
                  const style = STATUS_STYLE[status] || { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
                  return (
                    <Tr key={item._id}>
                      <Td><CellName>{item.name || '—'}</CellName></Td>
                      <Td><CellVersion>{item.lastVersion || '—'}</CellVersion></Td>
                      <Td><CellDate>{formatDate(item.lastPolicyVersionPublishedDate)}</CellDate></Td>
                      <Td>
                        {status
                          ? <StatusBadge $color={style.color} $bg={style.bg}>{status}</StatusBadge>
                          : <CellDate>—</CellDate>
                        }
                      </Td>
                      <Td>
                        <ActionBtns>
                          <Button
                            iconOnly
                            variant="icon"
                            type="button"
                            title={t('tooltips.show', 'Ver')}
                            onClick={() => navigate(`/policies/show/${item._id}`)}
                          >
                            <IconEye />
                          </Button>
                          <Button
                            iconOnly
                            variant="icon"
                            type="button"
                            title={t('common:tooltips.edit')}
                            onClick={() => navigate(`/policies/edit/${item._id}`)}
                          >
                            <EditSvg />
                          </Button>
                          {!item.policyVersions?.some(v => v.publishedDate != null) && (
                            <Button
                              iconOnly
                              variant="icon"
                              danger
                              type="button"
                              title={t('tooltips.delete', 'Excluir')}
                              onClick={() => onDelete(item)}
                            >
                              <IconTrash />
                            </Button>
                          )}
                        </ActionBtns>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </StyledTable>

            {totalPages > 0 && (
              <PaginationRow data-tour="pagination">
                <PaginationInfo>
                  {t('policy:page.pagination.showing', {
                    count: policies.length,
                    total: totalItems,
                  })}
                </PaginationInfo>
                <PaginationBtns>
                  <PageBtn
                    type="button"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <IconChevLeft />
                  </PageBtn>
                  {pageNumbers.map(n => (
                    <PageBtn
                      key={n}
                      type="button"
                      $active={n === page}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </PageBtn>
                  ))}
                  <PageBtn
                    type="button"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <IconChevRight />
                  </PageBtn>
                </PaginationBtns>
              </PaginationRow>
            )}
          </TableCard>
        )}

        <Dialog
          open={open}
          onConfirm={onConfirm}
          onCancel={onClose}
          message={`${t('policy:page.delete.confirm', 'Tem certeza que deseja excluir')} ${itemToDelete?.name}?`}
        />
      </PageWrap>

      <PageHelper steps={tourSteps} tourKey="policies-list" />
    </Container>
  );
};
